import {t, Trans} from '@lingui/macro';
import _, {isString} from 'lodash';
import React, {FC, useCallback} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {composerDetailsTokenBoxStyle} from '../../../../../../../libs/shared/ui/src/styles/composerStyles';
import {useLocalize} from '../../../../../../../srcCommon/internationalization/internationalizationContext';
import {TeammateModel} from '../../../../../../models/api/teammateModel';
import {TeammateToken} from '../../../../../common/editor/tokens/teammateToken';
import {TokenBase} from '../../../../../common/editor/tokens/tokenBase';
import {TokensEditor} from '../../../../../common/editor/tokens/tokensEditor';

/*
 * Props.
 */

interface InviteDropdownInviteHeaderProps {
  trackers: ReadonlyArray<TeammateModel | string>;
  searchText: string;
  onSearchTextChange: HandlerOf<string>;
  onSearchFocus?: Handler;
  onSearchBlur?: Handler;
  removeTracker: HandlerOf<ReadonlyArray<TeammateModel | string>>;
  canInviteVisitors: boolean;
}

/*
 * Style.
 */

const StyledHeaderDiv = styled.div`
  display: grid;
  grid-template: 'to editor';
  grid-template-columns: auto 1fr;
  grid-column-gap: 6px;

  padding: 7px 12px;
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
`;

const StyledRoleDiv = styled.div`
  grid-area: to;
  ${composerDetailsTokenBoxStyle};
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledEditorDiv = styled.div`
  grid-area: editor;
`;

/*
 * Component.
 */

export const InviteDropdownInviteHeader: FC<InviteDropdownInviteHeaderProps> = (props) => {
  const {trackers, removeTracker, canInviteVisitors} = props;
  const localize = useLocalize();

  /*
   * Event handlers.
   */

  const removeTrackers: HandlerOf<ReadonlyArray<number>> = useCallback(
    (trackerIndexes) => {
      const trackersToRemove = _(trackerIndexes)
        .map((index) => trackers[index])
        .compact()
        .value();

      removeTracker(trackersToRemove);
    },
    [removeTracker, trackers],
  );

  /*
   * Render.
   */

  const renderTracker = (index: number, isSelected: boolean) => {
    const tracker = trackers[index];

    // Visitor.
    if (isString(tracker)) {
      return <TokenBase isSelected={isSelected}>{tracker}</TokenBase>;
    }

    // Teammate.
    return <TeammateToken isSelected={isSelected} teammate={tracker} />;
  };

  const buildPlaceholder = () => {
    if (canInviteVisitors) {
      return localize(t('invite-header-placeholder-email')`teammate name or email address`);
    }

    return localize(t('invite-header-placeholder')`teammate name`);
  };

  const {searchText, onSearchTextChange, onSearchFocus, onSearchBlur} = props;
  const tokenIds = trackers.map((tracker) => (isString(tracker) ? tracker : String(tracker.id)));

  return (
    <StyledHeaderDiv>
      <StyledRoleDiv>
        <Trans id="invite-header-to">To:</Trans>
      </StyledRoleDiv>
      <StyledEditorDiv>
        <TokensEditor
          tokenIds={tokenIds}
          renderToken={renderTracker}
          onRemoveTokensClick={removeTrackers}
          text={searchText}
          onTextChange={onSearchTextChange}
          onFocus={onSearchFocus}
          onBlur={onSearchBlur}
          placeholder={buildPlaceholder()}
          shouldFocus
        />
      </StyledEditorDiv>
    </StyledHeaderDiv>
  );
};
